import { ArrowRight, Building2, CreditCard, ShieldCheck, Laptop, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import SEO from "@/components/SEO";

const supportPaths = [
  {
    title: "LightningFlow Platform",
    body: "Enterprise platform, deployment, nodes, licensing and demonstration enquiries.",
    action: "Contact LightningFlow Support",
    href: "mailto:contact@lightningflow.ai?subject=LightningFlow%20Platform%20support",
    icon: Building2,
    external: true,
  },
  {
    title: "Halo SuperVPN",
    body: "Connection status, diagnostics, supported devices and Halo SuperVPN product help.",
    action: "Open Halo SuperVPN Support",
    href: "/halo/support",
    icon: ShieldCheck,
  },
  {
    title: "SecureShield & SecureShield Pro",
    body: "Mac security checks, Network Guard, setup and product questions.",
    action: "Email SecureShield support",
    href: "mailto:contact@lightningflow.ai?subject=SecureShield%20support",
    icon: Laptop,
    external: true,
  },
  {
    title: "SecureAI",
    body: "Early-access path protection for compatible AI coding and agent workflows.",
    action: "Email SecureAI support",
    href: "mailto:contact@lightningflow.ai?subject=SecureAI%20support",
    icon: Sparkles,
    external: true,
  },
  {
    title: "Halo Account & Billing",
    body: "Subscription provider, plan, devices, downloads and account assistance.",
    action: "Open Halo Account",
    href: "/halo/account",
    icon: CreditCard,
  },
];

export default function Support() {
  const { pathname } = useLocation();
  const secureMacSupport = pathname === "/secureshield/support";

  return (
    <>
      <SEO
        title={secureMacSupport ? "SecureShield Support — LightningFlow" : "Support — LightningFlow"}
        description={secureMacSupport
          ? "Get help with SecureShield and SecureShield Pro security checks, supported remediation, installation and account downloads."
          : "Get support for the LightningFlow Platform, Halo SuperVPN, SecureShield, SecureAI and Halo Account."}
        canonicalPath={secureMacSupport ? "/secureshield/support" : "/support"}
        noIndex={secureMacSupport}
      />
      <section className="border-b border-ink-100 bg-ink-900 text-white">
        <div className="lf-container py-16 md:py-24">
          <div className="lf-eyebrow text-blue-200">LightningFlow Support</div>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-black leading-[0.96] md:text-6xl">
            Help that gets you moving.
          </h1>
          <p className="mt-7 max-w-3xl text-lg leading-relaxed text-ink-300 md:text-xl">
            Choose the product you use and go directly to the right support experience.
          </p>
          <p className="mt-5 text-sm text-ink-400">
            Looking for Halo SuperVPN help only?{" "}
            <Link className="underline underline-offset-4 text-cyan-200 hover:text-white" to="/halo/support">
              Open Halo SuperVPN Support
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="lf-section border-b border-ink-100 scroll-mt-28">
        <div className="lf-container">
          <div className="grid border border-ink-100 bg-white md:grid-cols-2 xl:grid-cols-3">
            {supportPaths.map((path, index) => {
              const Icon = path.icon;
              const content = (
                <>
                  <Icon className="h-6 w-6 text-electric" strokeWidth={1.75} />
                  <h2 className="mt-8 font-display text-2xl font-bold">
                    {path.title}
                  </h2>
                  <p className="mt-4 min-h-20 text-sm leading-relaxed text-ink-600">
                    {path.body}
                  </p>
                  <span className="lf-link mt-7">
                    {path.action} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </>
              );

              const className = `block p-7 transition-colors hover:bg-ink-50 md:p-8 border-ink-100 ${
                index > 0 ? "border-t md:border-l xl:border-t-0" : ""
              } ${index >= 2 ? "md:border-t xl:border-t-0" : ""} ${index === 2 ? "md:border-l-0 xl:border-l" : ""} ${index >= 3 ? "xl:border-t" : ""}`;

              return path.external ? (
                <a key={path.title} href={path.href} className={className}>
                  {content}
                </a>
              ) : (
                <Link key={path.title} to={path.href} className={className}>
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lf-section bg-ink-50/50">
        <div className="lf-container grid gap-10 lg:grid-cols-[.72fr_1.28fr] lg:items-center">
          <div>
            <div className="lf-eyebrow">General enquiries</div>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight md:text-5xl">
              One contact. A clear next step.
            </h2>
          </div>
          <div className="border border-ink-100 bg-white p-7 md:p-8">
            <p className="text-lg leading-relaxed text-ink-600">
              For product, sales, privacy or general support, contact our
              Australian team and include the product name and a short
              description of what you need.
            </p>
            <a
              className="lf-btn-primary mt-7"
              href="mailto:contact@lightningflow.ai"
            >
              contact@lightningflow.ai <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
